/**
 * AxLabs Experiments - Newton's Second Law with Friction
 *
 * Demonstrates F = ma with realistic friction models.
 * Includes static/kinetic friction transition.
 *
 * Governing equations:
 * - F_net = ma
 * - f_s ≤ μ_s * N (static friction)
 * - f_k = μ_k * N (kinetic friction)
 */
import { BaseSimulationEngine, DIMENSIONS, } from '@axlabs/simulation-core';
// ============================================================================
// PARAMETERS
// ============================================================================
const PARAMETERS = [
    {
        id: 'mass',
        name: 'Mass',
        symbol: 'm',
        unit: 'kg',
        dimension: DIMENSIONS.MASS,
        min: 0.1,
        max: 100,
        default: 5,
        step: 0.5,
        description: 'Mass of the object',
    },
    {
        id: 'appliedForce',
        name: 'Applied Force',
        symbol: 'F_{app}',
        unit: 'N',
        dimension: DIMENSIONS.FORCE,
        min: 0,
        max: 500,
        default: 20,
        step: 1,
        description: 'Horizontal force applied to the object',
    },
    {
        id: 'staticFriction',
        name: 'Static Friction Coefficient',
        symbol: '\\mu_s',
        unit: '',
        dimension: DIMENSIONS.DIMENSIONLESS,
        min: 0,
        max: 1.5,
        default: 0.4,
        step: 0.05,
        description: 'Coefficient of static friction',
    },
    {
        id: 'kineticFriction',
        name: 'Kinetic Friction Coefficient',
        symbol: '\\mu_k',
        unit: '',
        dimension: DIMENSIONS.DIMENSIONLESS,
        min: 0,
        max: 1.2,
        default: 0.3,
        step: 0.05,
        description: 'Coefficient of kinetic friction (must be ≤ static)',
    },
    {
        id: 'inclineAngle',
        name: 'Incline Angle',
        symbol: '\\theta',
        unit: '°',
        dimension: DIMENSIONS.DIMENSIONLESS,
        min: 0,
        max: 60,
        default: 0,
        step: 1,
        description: 'Angle of inclined plane (0 = horizontal)',
    },
    {
        id: 'gravity',
        name: 'Gravitational Acceleration',
        symbol: 'g',
        unit: 'm/s²',
        dimension: DIMENSIONS.ACCELERATION,
        min: 1,
        max: 25,
        default: 9.81,
        step: 0.01,
        description: 'Local gravitational acceleration',
    },
];
// ============================================================================
// EQUATIONS
// ============================================================================
const EQUATIONS = [
    {
        id: 'newtons_second',
        name: "Newton's Second Law",
        latex: 'F_{net} = ma',
        variables: ['netForce', 'mass', 'a'],
        description: 'Net force equals mass times acceleration',
    },
    {
        id: 'normal_force',
        name: 'Normal Force',
        latex: 'N = mg\\cos\\theta',
        variables: ['mass', 'gravity', 'inclineAngle'],
        description: 'Normal force on inclined plane',
    },
    {
        id: 'static_friction',
        name: 'Static Friction',
        latex: 'f_s \\leq \\mu_s N',
        variables: ['staticFriction'],
        description: 'Maximum static friction before motion begins',
        condition: (params) => (params['appliedForce'] ?? 0) < (params['staticFriction'] ?? 0.4) * (params['mass'] ?? 5) * (params['gravity'] ?? 9.81),
    },
    {
        id: 'kinetic_friction',
        name: 'Kinetic Friction',
        latex: 'f_k = \\mu_k N',
        variables: ['kineticFriction'],
        description: 'Kinetic friction during motion',
        condition: (params) => (params['appliedForce'] ?? 0) >= (params['staticFriction'] ?? 0.4) * (params['mass'] ?? 5) * (params['gravity'] ?? 9.81),
    },
    {
        id: 'net_force_incline',
        name: 'Net Force on Incline',
        latex: 'F_{net} = F_{app} - mg\\sin\\theta - f',
        variables: ['appliedForce', 'mass', 'gravity', 'inclineAngle', 'frictionForce'],
        description: 'Net force considering gravity component and friction',
        condition: (params) => (params['inclineAngle'] ?? 0) > 0,
    },
    {
        id: 'acceleration',
        name: 'Acceleration',
        latex: 'a = \\frac{F_{net}}{m}',
        variables: ['a', 'netForce', 'mass'],
        description: 'Acceleration from net force',
    },
    {
        id: 'kinetic_energy',
        name: 'Kinetic Energy',
        latex: 'KE = \\frac{1}{2}mv^2',
        variables: ['mass', 'v'],
        description: 'Energy due to motion',
    },
    {
        id: 'work_friction',
        name: 'Work by Friction',
        latex: 'W_f = -f_k \\cdot d',
        variables: ['frictionForce', 'x'],
        description: 'Energy dissipated by friction (negative work)',
    },
];
// ============================================================================
// FAILURE MODES
// ============================================================================
const FAILURE_MODES = [
    {
        id: 'excessive_speed',
        name: 'Excessive Velocity',
        description: 'Velocity has become unrealistically high',
        check: (state) => Math.abs(state.variables['v'] ?? 0) > 100,
        severity: 'warning',
        educationalMessage: 'The velocity has reached an unrealistic value. ' +
            'In real experiments, air resistance and other factors would limit speed.',
    },
    {
        id: 'friction_violation',
        name: 'Friction Coefficient Error',
        description: 'Kinetic friction should not exceed static friction',
        check: (_, params) => (params['kineticFriction'] ?? 0) > (params['staticFriction'] ?? 0),
        severity: 'warning',
        educationalMessage: 'Kinetic friction coefficient cannot exceed static friction. ' +
            'Static friction is always ≥ kinetic friction for real materials.',
    },
];
// ============================================================================
// EXPERIMENT CONFIG
// ============================================================================
export const NEWTONS_LAW_CONFIG = {
    id: 'newtons-second-law',
    slug: 'newtons-second-law',
    name: "Newton's Second Law with Friction",
    domain: 'physics',
    description: 'Explore F = ma with realistic friction',
    longDescription: `
Newton's Second Law states that the acceleration of an object is proportional
to the net force acting on it and inversely proportional to its mass.

This experiment includes:
- Static vs kinetic friction transition
- Inclined plane mechanics
- Force diagrams
- Energy dissipation by friction

Key concepts:
- Static friction prevents motion until overcome
- Kinetic friction acts during motion
- μ_k < μ_s for real materials
- On inclines, gravity has parallel and normal components
- Work-energy theorem: friction dissipates kinetic energy
  `.trim(),
    version: 1,
    conceptTags: ['force', 'friction', 'acceleration', 'incline', 'work-energy'],
    difficulty: 3,
    prerequisites: ['kinematics', 'vectors'],
    parameters: PARAMETERS,
    equations: EQUATIONS,
    initialConditions: [
        { variableId: 'x', type: 'fixed', value: 0 },
        { variableId: 'v', type: 'fixed', value: 0 },
    ],
    failureModes: FAILURE_MODES,
    recommendedDt: 1 / 120,
    maxDt: 1 / 30,
};
// ============================================================================
// SIMULATION ENGINE
// ============================================================================
export class NewtonsLawSimulation extends BaseSimulationEngine {
    config = NEWTONS_LAW_CONFIG;
    /** Track whether object was moving in previous step */
    wasMoving = false;
    constructor() {
        super({ x: 0, v: 0, a: 0, netForce: 0, frictionForce: 0, isMoving: 0 }, { type: 'euler', dt: 1 / 120, maxSubsteps: 10 });
    }
    getInitialState(_params) {
        return {
            x: 0,
            v: 0,
            a: 0,
            netForce: 0,
            frictionForce: 0,
            isMoving: 0,
        };
    }
    /**
     * Calculate friction force based on motion state
     */
    calculateFriction(v, appliedForce, params) {
        const m = params['mass'] ?? 5;
        const g = params['gravity'] ?? 9.81;
        const muS = params['staticFriction'] ?? 0.4;
        const muK = params['kineticFriction'] ?? 0.3;
        const theta = (params['inclineAngle'] ?? 0) * Math.PI / 180;
        // Normal force
        const N = m * g * Math.cos(theta);
        // Gravity component along incline
        const gravityParallel = m * g * Math.sin(theta);
        // Total force trying to move the object (ignoring friction)
        const drivingForce = appliedForce - gravityParallel;
        // Maximum static friction
        const maxStaticFriction = muS * N;
        // Kinetic friction magnitude
        const kineticFriction = muK * N;
        const VELOCITY_THRESHOLD = 0.001;
        if (Math.abs(v) > VELOCITY_THRESHOLD || this.wasMoving) {
            // Object is moving - apply kinetic friction
            // Friction opposes motion direction
            const frictionDir = v > 0 ? -1 : 1;
            // Check if friction would cause reversal (object should stop)
            if (Math.abs(v) < 0.01 && Math.abs(drivingForce) < kineticFriction) {
                // Object is slowing to a stop
                this.wasMoving = false;
                return { friction: drivingForce, isMoving: false };
            }
            this.wasMoving = true;
            return { friction: frictionDir * kineticFriction, isMoving: true };
        }
        else {
            // Object is stationary - static friction
            if (Math.abs(drivingForce) > maxStaticFriction) {
                // Overcome static friction - start moving
                this.wasMoving = true;
                const frictionDir = drivingForce > 0 ? -1 : 1;
                return { friction: frictionDir * kineticFriction, isMoving: true };
            }
            else {
                // Static friction matches and opposes driving force
                this.wasMoving = false;
                return { friction: -drivingForce, isMoving: false };
            }
        }
    }
    computeDerivatives(state, params) {
        const m = params['mass'] ?? 5;
        const F_app = params['appliedForce'] ?? 20;
        const g = params['gravity'] ?? 9.81;
        const theta = (params['inclineAngle'] ?? 0) * Math.PI / 180;
        // Gravity component along incline
        const gravityParallel = m * g * Math.sin(theta);
        // Calculate friction
        const { friction, isMoving } = this.calculateFriction(state.v, F_app, params);
        // Net force
        const F_net = F_app - gravityParallel + friction;
        // Acceleration
        const a = F_net / m;
        return {
            x: state.v,
            v: isMoving ? a : 0,
            a: isMoving ? a : 0,
            netForce: F_net,
            frictionForce: Math.abs(friction),
            isMoving: isMoving ? 1 : 0,
        };
    }
    integrate(dt) {
        const derivs = this.computeDerivatives(this.state, this.parameters);
        // Semi-implicit Euler
        this.state.v += (derivs.v ?? 0) * dt;
        this.state.x += this.state.v * dt;
        // Update other state variables
        this.state.a = derivs.a ?? 0;
        this.state.netForce = derivs.netForce ?? 0;
        this.state.frictionForce = derivs.frictionForce ?? 0;
        this.state.isMoving = derivs.isMoving ?? 0;
        // Prevent negative position if desired
        if (this.state.x < 0) {
            this.state.x = 0;
            if (this.state.v < 0) {
                this.state.v = 0;
            }
        }
    }
    computeEnergy(state, params) {
        const m = params['mass'] ?? 5;
        const g = params['gravity'] ?? 9.81;
        const theta = (params['inclineAngle'] ?? 0) * Math.PI / 180;
        const kinetic = 0.5 * m * state.v * state.v;
        // Potential energy for incline (h = x * sin(theta))
        const potential = m * g * state.x * Math.sin(theta);
        return {
            kinetic,
            potential,
            thermal: 0, // Friction work is dissipated
            total: kinetic + potential,
        };
    }
    getPositionForStabilityCheck() {
        return this.state.x;
    }
    /**
     * Check if applied force overcomes static friction
     */
    willOvercomeStaticFriction() {
        const m = this.parameters['mass'] ?? 5;
        const g = this.parameters['gravity'] ?? 9.81;
        const muS = this.parameters['staticFriction'] ?? 0.4;
        const theta = (this.parameters['inclineAngle'] ?? 0) * Math.PI / 180;
        const F_app = this.parameters['appliedForce'] ?? 20;
        const N = m * g * Math.cos(theta);
        const gravityParallel = m * g * Math.sin(theta);
        const maxStaticFriction = muS * N;
        return Math.abs(F_app - gravityParallel) > maxStaticFriction;
    }
    /**
     * Calculate terminal acceleration (if friction is overcome)
     */
    getAcceleration() {
        if (!this.willOvercomeStaticFriction()) {
            return 0;
        }
        const m = this.parameters['mass'] ?? 5;
        const g = this.parameters['gravity'] ?? 9.81;
        const muK = this.parameters['kineticFriction'] ?? 0.3;
        const theta = (this.parameters['inclineAngle'] ?? 0) * Math.PI / 180;
        const F_app = this.parameters['appliedForce'] ?? 20;
        const N = m * g * Math.cos(theta);
        const gravityParallel = m * g * Math.sin(theta);
        const kineticFriction = muK * N;
        const F_net = F_app - gravityParallel - kineticFriction;
        return F_net / m;
    }
}
/**
 * Factory function
 */
export function createNewtonsLaw() {
    return new NewtonsLawSimulation();
}
//# sourceMappingURL=newtons-second-law.js.map